/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import { Text } from '@/shared/ui';
import {
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
} from '@/shared/ui';
import { EmptyState } from '@/shared/ui/components/basic/EmptyState';
import type { PartnershipResult } from '../context/PartnershipsWizardContext';
import { ResultsGroup } from './ResultsGroup';
import {
  advertiserBreakdown,
  clusterResultsByGeo,
  presentAdvertiserBuckets,
} from '../utils/results-grouping';
import { DataState } from '@/shared/ui/components/basic/data-state/DataState';
import { CardListSkeleton } from '@/shared/ui';

interface ResultsListProps {
  results: PartnershipResult[];
  searched: boolean;
  loading: boolean;
  error: string | null;
  onRetry?: () => void;
}

/**
 * Pre-search informational empty state ("no searches performed yet"), reusing
 * the shared {@link EmptyState} component so Partnerships matches the clients /
 * portfolio ("nenhum cliente na carteira") module exactly.
 *
 * @returns The shared empty-state surface for the pre-search state.
 */
function NoSearchYetState() {
  const { t } = useTranslation('features/partnerships');
  return (
    <EmptyState
      variant="dashed"
      icon={
        <MagnifyingGlassIcon
          width={28}
          height={28}
          style={{ color: 'var(--seed-base)', opacity: 0.5 }}
        />
      }
      title={t('results.no_search_title')}
      description={t('results.no_search_description')}
    />
  );
}

/**
 * Polished "search failed" state built on the shared {@link EmptyState}: a
 * warning icon, a clear title, the resolved error message and the retry action
 * — consistent with the design system and {@link NoSearchYetState}.
 *
 * @param props - The resolved error message and optional retry callback.
 * @returns The shared error-state surface.
 */
function SearchFailedState({
  message,
  onRetry,
}: {
  message: string;
  onRetry?: () => void;
}) {
  const { t } = useTranslation('features/partnerships');
  const action = onRetry
    ? { label: t('results.retry'), onClick: onRetry }
    : undefined;
  return (
    <div data-testid="partnerships-search-failed" className="flex min-h-0 flex-1 flex-col">
      <EmptyState
        icon={
          <ExclamationTriangleIcon
            width={28}
            height={28}
            style={{ color: 'var(--seed-base)', opacity: 0.7 }}
          />
        }
        title={t('results.error_title')}
        description={message}
        action={action}
      />
    </div>
  );
}

/**
 * Renders the partner-search results collection, delegating loading/error/empty
 * states to {@link DataState} and mapping each result to a {@link ResultCard}.
 *
 * @param props - The results, search/loading flags, error, and retry callback.
 * @returns The results list surface.
 */
export function ResultsList({ results, searched, loading, error, onRetry }: ResultsListProps) {
  const { t } = useTranslation('features/partnerships');

  if (!searched && !loading) {
    return <NoSearchYetState />;
  }

  return (
    <DataState
      isLoading={loading}
      isError={!!error}
      isEmpty={searched && results.length === 0}
      loadingFallback={<CardListSkeleton count={3} />}
      errorState={
        <SearchFailedState message={error ?? t('results.error')} onRetry={onRetry} />
      }
      emptyState={
        <div className="rounded-(--radius-lg) border border-[var(--border-subtle)] p-[var(--space-6)] text-center text-[var(--text-secondary)]">
          {t('results.empty')}
        </div>
      }
    >
      <ResultsCollection results={results} />
    </DataState>
  );
}

/**
 * The populated results surface: a single compact summary line (total count +
 * advertiser breakdown as dot-separated tokens) followed by the results grouped
 * into geographic clusters, each with its own count. The summary is one tight,
 * scannable row — no large empty tiles, no misleading "N imobiliárias" figure —
 * so the top of the listing stays dense and correct.
 *
 * @param props - The completed, non-empty result set.
 * @returns The grouped results collection.
 */
function ResultsCollection({ results }: { results: PartnershipResult[] }) {
  const { t } = useTranslation('features/partnerships');
  const countLabel = (count: number) =>
    `${count} ${t('summary.properties', { count })}`;
  const clusters = clusterResultsByGeo(results, t('page_title'));
  const breakdown = advertiserBreakdown(results);
  const buckets = presentAdvertiserBuckets(breakdown);

  return (
    <div className="flex flex-col gap-[var(--space-6)]">
      <div
        className="flex flex-wrap items-baseline gap-x-[var(--space-2)] gap-y-[var(--space-1)]"
        data-testid="results-summary"
      >
        <Text variant="body" size="sm" fw={600}>
          {countLabel(results.length)}
        </Text>
        {buckets.length > 0 && (
          <Text
            span
            className="flex flex-wrap items-baseline gap-x-[var(--space-2)] gap-y-[var(--space-1)] text-[var(--text-tertiary)]"
            data-testid="results-advertiser-breakdown"
          >
            {buckets.map(([bucket, count]) => (
              <Text key={bucket} span size="xs" fw={500} tone="tertiary">
                · {count} {t(`results.advertiser_${bucket}`)}
              </Text>
            ))}
          </Text>
        )}
      </div>
      {clusters.map((cluster) => (
        <ResultsGroup key={cluster.key} cluster={cluster} countLabel={countLabel} />
      ))}
    </div>
  );
}
